import { translate } from '@openclassrooms/translations';

import FundingMechanisms from './fundingMechanisms';
import thanksUrl from '../images/thanks.svg';
import applicationFilesUrl from '../images/applicationFiles.svg';
import appsourceConfig from '../types/appsourceConfig';
import RecordTypes from './recordTypes';

const DEFAULT_VALUES = {
  // Globals
  recordTypeId: RecordTypes.Apprenticeship,
  fundingMechanism: FundingMechanisms.Default,
  isSelectedPathRequired: false,
  shouldDisplayStepExplainer: true,
  // Welcome
  isB2bSourceType: false,
  hasUnemploymentCertificate: true,
  // Interests screen
  hasPathDuration: true,
  shouldUseApprenticeshipDuration: true,
  shouldOnlyShowPathsInUsersLanguage: false,
  hasMultiPath: true,
  // Thanks screen
  hasAdmissionProcess: true,
  thanksPictureUrl: thanksUrl,
  hasSpamWarning: false,
};

const EMPLOYER_STANDARD = {
  ...DEFAULT_VALUES,
  recordTypeId: RecordTypes.Employer,
  // Welcome
  hasUnemploymentCertificate: false,
  isB2bSourceType: true,
  // Interests screen
  hasPathDuration: false,
  hasMultiPath: false,
  // Thanks screen
  hasAdmissionProcess: false,
};

export const APPSOURCE_NAMES = {
  US_APPRENTICESHIP: 'us-apprenticeship',
  GUILD: 'guild',
  AMAZON: 'amazon',
  DELIVEROO: 'deliveroo',
  VAE: 'vae',
  GENERIC: 'generic',
};

const getAppSourcesConfig = (): Record<string, appsourceConfig> => ({
  [APPSOURCE_NAMES.US_APPRENTICESHIP]: {
    ...DEFAULT_VALUES,
    appSource: APPSOURCE_NAMES.US_APPRENTICESHIP,
    fundingMechanism: FundingMechanisms.Apprenticeship,
    hasMultiPath: false,
    recordTypeId: RecordTypes.Employer,
    isSelectedPathRequired: true,
    paths: [521, 544, 545, 546, 547, 550, 551, 552],
    shouldDisplayStepExplainer: false,
    thanksPictureUrl: applicationFilesUrl,
    unhandledErrorMessage: translate('application.step.error.api-call.us-specific'),
  },
  [APPSOURCE_NAMES.VAE]: {
    ...DEFAULT_VALUES,
    appSource: APPSOURCE_NAMES.VAE,
    recordTypeId: RecordTypes.VAE,
    customThanksTitle: translate('application.thanks.title-vae'),
    customThanksMessage: () => translate('application.thanks.subtitle-vae'),
    hasSpamWarning: true,
    hasMultiPath: false,
    thanksPictureUrl: applicationFilesUrl,
  },
  [APPSOURCE_NAMES.GENERIC]: {
    ...DEFAULT_VALUES,
    recordTypeId: RecordTypes.Generic,
    appSource: APPSOURCE_NAMES.GENERIC,
    hasPathDuration: false,
    shouldUseApprenticeshipDuration: false,
    thanksPictureUrl: applicationFilesUrl,
    hasTrustPilotWidget: true,
    customThanksMessage: (pathName) =>
      translate('application.thanks.messages.generic.thanks', {
        pathName,
      }),
  },

  /**
   * Employer
   */
  [APPSOURCE_NAMES.GUILD]: {
    ...EMPLOYER_STANDARD,
    appSource: APPSOURCE_NAMES.GUILD,
    name: 'Guild Education',
    paths: [
      76, 144, 146, 167, 198, 199, 200, 204, 205, 210, 241, 242, 243, 244, 245, 246, 247, 248, 249,
      250, 251, 252, 253, 254, 255, 256, 258, 261, 274, 275, 276, 283, 284, 287, 300, 305, 307, 308,
      311, 347, 350, 374, 375, 376, 377, 378, 478, 479, 482, 509, 526, 543, 546, 554, 555, 571, 607,
      608, 702,
    ], // archived: 144
  },
  [APPSOURCE_NAMES.AMAZON]: {
    ...EMPLOYER_STANDARD,
    appSource: APPSOURCE_NAMES.AMAZON,
    name: 'Amazon Career Choice Program',
    paths: [496, 544, 545, 768, 769, 770],
    shouldOnlyShowPathsInUsersLanguage: true,
  },
  [APPSOURCE_NAMES.DELIVEROO]: {
    ...EMPLOYER_STANDARD,
    appSource: APPSOURCE_NAMES.DELIVEROO,
    name: 'Deliveroo',
    paths: [141, 287, 330, 331, 333, 347, 395, 496, 536, 544, 547, 555, 594, 595, 717, 733],
    shouldOnlyShowPathsInUsersLanguage: true,
  },

  // For testing purposes
  MINI_FUNNEL: {
    ...DEFAULT_VALUES,
    appSource: 'minifunnel',
    name: 'Testing mini funnel',
  },
});

export default getAppSourcesConfig;
